import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Divider, FormControlLabel, FormControl, FormLabel, FormGroup, Grid, IconButton, Switch, Paper, Chip, Radio, RadioGroup, Select, MenuItem, InputLabel, CircularProgress, Tooltip } from '@mui/material';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Tabs, Tab } from '@mui/material';
import useApi from '../../shared/hooks/useApi';
import SchedulerTable from './SchedulerTable';
import QuickSimulationSetup from './QuickSimulationSetup';
import { styled } from '@mui/material/styles';
import { tokens } from '../../../theme';
import { useSettings } from '../context/SettingsContext';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
// Import icons for different categories
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSimulation } from '../context/SimulationContext';
import dayjs from 'dayjs';

const getCategoryConfig = (theme) => {
    const colors = tokens(theme.palette.mode);

    return {
        'Photovoltaïc installation': {
            icon: SolarPowerIcon,
            color: '#8978A8',        // softer muted purple
            borderColor: '#E4DEEF',
            backgroundColor: 'rgba(228, 222, 239, 0.25)'
        },
        'Battery Energy Storage System': {
            icon: BatteryChargingFullIcon,
            color: '#748599',        // softer slate blue-gray
            borderColor: '#E0E5EB',
            backgroundColor: 'rgba(224, 229, 235, 0.25)'
        },
        'Grid connection': {
            icon: ElectricBoltIcon,
            color: '#6A7CA8',        // softer navy/blue
            borderColor: '#E1E6F1',
            backgroundColor: 'rgba(225, 230, 241, 0.25)'
        },
        'Local electricity consumption': {
            icon: HomeIcon,
            color: '#A88F73',        // softer amber-brown
            borderColor: '#EFE8E1',
            backgroundColor: 'rgba(239, 232, 225, 0.25)'
        },
        'Energy contract & markets': {
            icon: TrendingUpIcon,
            color: '#A67C7C',        // softer burgundy/rose
            borderColor: '#F0E3E3',
            backgroundColor: 'rgba(240, 227, 227, 0.25)'
        },
        'Start simulation ': {
            icon: PlayCircleOutlineIcon,
            color: '#6F8E91',        // softer muted teal
            borderColor: '#E3ECEC',
            backgroundColor: 'rgba(227, 236, 236, 0.25)'
        }
    };
};





const CategoryContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'borderColor' && prop !== 'bgcolor',
})(({ theme, borderColor, bgcolor }) => ({
    padding: '26px',
    border: `1.5px solid ${borderColor}`,
    marginBottom: '28px',
    borderRadius: '18px',
    backgroundColor: bgcolor || 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
        //transform: 'translateY(-2px)',
        boxShadow: `0 10px 30px ${bgcolor}80`,
        borderColor: "#635781"
    }
}));

const CategoryHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '22px',
    paddingBottom: '18px',
    borderBottom: `1.5px solid ${color}30`
}));

const ModalHeader = styled(Box)(({ theme }) => {
    const colors = tokens(theme.palette.mode);
    return {
        background: `linear-gradient(135deg, ${colors.entras_purple[100]} 0%, ${colors.entras_purple[300]} 100%)`,
        color: 'white',
        padding: '24px',
        position: 'relative'
    };
});
const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.1)'
    }
}));
const toBrusselsISO = (dateStr) => {
    const date = new Date(dateStr);
  
    // Brüksel saatine göre yeni bir tarih oluştur
    const brussels = new Date(
      date.toLocaleString("en-US", { timeZone: "Europe/Brussels" })
    );
  
    // Brüksel timezone offset'ini dakika cinsinden al (eksi değer = ileri saat)
    const offsetMinutes = -brussels.getTimezoneOffset();
    const offsetHours = String(Math.floor(offsetMinutes / 60)).padStart(2, "0");
    const offsetMins = String(offsetMinutes % 60).padStart(2, "0");
  
    const offsetSign = offsetMinutes >= 0 ? "+" : "-";
    const timezoneOffset = `${offsetSign}${offsetHours}:${offsetMins}`;
  
    // ISO formatı T ve offset ile oluştur
    const iso = brussels.toISOString().split(".")[0] + timezoneOffset;
  
    return iso;
  }

const SimulationSettingsModal = ({ open, onClose, setLoading, triggerAlert }) => {
    // Helper function to format category names
    const formatCategoryName = (categoryName) => {
        if (!categoryName) return '';

        // Handle special cases first
        const specialCases = {
            'grid_connection': 'Grid Connection',
            'pv_configuration': 'PV Configuration',
            'battery_energy_storage_system': 'Battery Energy Storage System',
            'electricity_consumption': 'Electricity Consumption',
            'electricity_market': 'Electricity Market',
            'financial': 'Financial',
            'simulation_config': 'Simulation Configuration'
        };

        if (specialCases[categoryName]) {
            return specialCases[categoryName];
        }

        // Convert snake_case to Title Case
        return categoryName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Get settings from context
    const {
        settings,
        originalSettings,
        settingsValues,
        setSettingsValues,
        uploadedFiles,
        setUploadedFiles,
        updateUploadedFile,
        isLoadingSettings,
        updateSettingValue,
        resetSettings
    } = useSettings();

    const { apiPost, apiGet, loadingApi } = useApi();
    const [currentTab, setCurrentTab] = useState(0);
    const fileInputRefs = React.useRef({});
    const [isLoading, setIsLoading] = useState(false);
    const [visibleSchedulers, setVisibleSchedulers] = useState(new Set());
    const { addPendingSimulation } = useSimulation();


    const theme = { palette: { mode: 'light' } };
    const categoryConfig = getCategoryConfig(theme);
    const roleBasedSettings = Object.entries(settings).reduce((acc, [key, setting]) => {
        const category = setting['Category'];
        const roleLabel = setting['role_label'] || 'General';

        if (!acc[roleLabel]) {
            acc[roleLabel] = {};
        }
        if (!acc[roleLabel][category]) {
            acc[roleLabel][category] = [];
        }

        acc[roleLabel][category].push({
            key,
            variableName: setting['Variable Name'],
            displayName: setting['Display name (label)'],
            category: setting['Category'],
            detailedName: setting['Detailed name (under parameter box)'],
            defaultValue: setting['Default value'],
            type: setting['Type'],
            additionalInfo: setting['Additional info'],
            role: setting['Role'],
            roleLabel: setting['role_label'],
            selections: setting['selections']
        });
        return acc;
    }, {});

    const roles = Object.keys(roleBasedSettings).sort((a, b) => {
        if (a.toLowerCase() === 'operator') return -1;
        if (b.toLowerCase() === 'operator') return 1;
        return a.localeCompare(b);
    });

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const closeModal = () => {
        onClose();
    }

    const handleChange = (key, value) => {
        updateSettingValue(key, value);
    };

    const generateSimulationId = () => {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substr(2, 5);
        return `SIM_${timestamp}_${random}`.toUpperCase();
    };

    const handleFileUpload = async (type, file) => {
        if (file) {
            try {
                // Check file size (limit to 10MB)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    alert(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the maximum allowed size of 10MB`);
                    return;
                }

                // Convert file to base64 for proper serialization
                const reader = new FileReader();
                const fileData = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => {
                        console.error('FileReader error:', error);
                        reject(error);
                    };
                    reader.readAsDataURL(file);
                });

                const fileInfo = {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    content: fileData,
                    originalFile: file
                };

                updateUploadedFile(type, fileInfo);
            } catch (error) {
                console.error('Error reading file:', error);
                alert('Error reading file. Please try again.');
            }
        } else {
            updateUploadedFile(type, null);
        }
    };
    const formatSettings = (settingsMetadata, currentValues) => {
        const formatted = {};
        Object.entries(settingsMetadata).forEach(([key, metadata]) => {
            const category = metadata['Category'];
            let value = currentValues[key] !== undefined ? currentValues[key] : metadata['Default value'];

            const categoryKeyMap = {
                'Start simulation ': 'simulation_config',
                'Photovoltaïc installation': 'pv_configuration',
                'Grid connection': 'grid_connection',
                'Local electricity consumption': 'electricity_consumption',
                'Energy contract & markets': 'electricity_market',
                'Battery Energy Storage System': 'battery_energy_storage_system',
                'financial': 'financial',
                'battery_energy_storage_system': 'battery_energy_storage_system',
                'pv_configuration': 'pv_configuration',
                'grid_connection': 'grid_connection',
                'electricity_consumption': 'electricity_consumption',
                'electricity_market': 'electricity_market',
                'simulation_config': 'simulation_config'
            };

            const categoryKey = categoryKeyMap[category] || category.toLowerCase().replace(/ /g, '_');

            if (!formatted[categoryKey]) {
                formatted[categoryKey] = {};
            }

            if (metadata['Type'] === 'scheduler') {
                let scheduleObj = value;
                if (typeof value === 'string') {
                    try {
                        scheduleObj = JSON.parse(value);
                    } catch (e) {
                        console.error('Error parsing scheduler value:', e);
                        scheduleObj = {};
                    }
                }

                if (Array.isArray(scheduleObj)) {
                    formatted[categoryKey][key] = scheduleObj;
                } else if (typeof scheduleObj === 'object' && scheduleObj !== null) {
                    const schedulerArray = new Array(168).fill(0);
                    Object.entries(scheduleObj).forEach(([cellKey, isActive]) => {
                        if (isActive) {
                            const [day, hour] = cellKey.split('-').map(Number);
                            const index = day * 24 + hour;
                            if (index >= 0 && index < 168) {
                                schedulerArray[index] = 1;
                            }
                        }
                    });
                    formatted[categoryKey][key] = schedulerArray;
                } else {
                    formatted[categoryKey][key] = new Array(168).fill(0);
                }
            } else {
                // Fix date format and convert to Europe/Brussels ISO if needed
                if (typeof value === 'string' && (key.includes('_date') || key.includes('_iso'))) {
                    const brusselsIso = toBrusselsISO(value);
                    console.log(`Fixed date format for ${key}:`, brusselsIso);
                    value = brusselsIso;
                }
                formatted[categoryKey][key] = value;
            }
        });
        return formatted;
    }


    const handleSimulate = async () => {
        const formattedSettings = formatSettings(settings, settingsValues);

        if (!formattedSettings.simulation_config.simulation_unique_id || !formattedSettings.simulation_config.simulation_from_date_iso || !formattedSettings.simulation_config.simulation_to_date_iso) {
            alert('Please fill in all required simulation fields (Simulation ID, Start Date, End Date)');
            return;
        }
        console.log("formattedSettings55", formattedSettings);
        const requiredFileTypes = new Set();
        Object.entries(settingsValues).forEach(([key, value]) => {
            if (value === "Based on input file") {
                if (key.toLowerCase().includes('pv') || key.toLowerCase().includes('generation')) {
                    requiredFileTypes.add('pvGeneration');
                } else if (key.toLowerCase().includes('consumption')) {
                    requiredFileTypes.add('consumption');
                } else if (key.toLowerCase().includes('price') || key.toLowerCase().includes('market')) {
                    requiredFileTypes.add('prices');
                } else {
                    requiredFileTypes.add('data');
                }
            }
        });

        const requiredFileTypesArray = Array.from(requiredFileTypes);

        if (requiredFileTypesArray.length > 0) {
            const hasAnyFile = Object.keys(uploadedFiles).length > 0;

            if (!hasAnyFile) {
                alert('Please upload a data file for your configuration');
                return;
            }
        }

        setIsLoading(true);
        setLoading(true);

        // Create temporary pending simulation
        const tempSimulationId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const simulationName = formattedSettings.simulation_config.simulation_unique_id;
        
        const pendingSimulation = {
            event_id: tempSimulationId,
            created: new Date().toISOString(),
            message: JSON.stringify({
                name: simulationName,
                status: "Pending",
                s3_key: "",
                "ux-filters": {}
            }),
            partition_key: "ESID-25-006:SIMULATION#PENDING",
            ip_address: "",
            timestamp: Date.now(),
            isPending: true
        };
        console.log("pendingSimulation55", pendingSimulation);
        // Add to localStorage and context immediately
        addPendingSimulation(pendingSimulation);

        // Close modal and reset loading states immediately
        onClose();
        setIsLoading(false);
        setLoading(false);

        // Continue with file upload and simulation in the background
        (async () => {
            try {
                let presigned_url = null;
                let s3_key = null;

                // Only attempt file upload if files are required
                if (requiredFileTypesArray.length > 0) {
                    const fileEntries = Object.entries(uploadedFiles);
                    if (fileEntries.length === 0) {
                        console.error('No files uploaded for simulation');
                        return;
                    }

                    const [fileType, fileInfo] = fileEntries[0];
                    const presignedUrlResponse = await apiPost("UX_HTTP_API", "/esid-25-006/get-presigned-upload-url", {
                        filename: fileInfo.name,
                        content_type: fileInfo.type
                    });

                    const { presigned_post, presigned_download_url, s3_key: response_s3_key, bucket_name } = presignedUrlResponse;
                    const base64Data = fileInfo.content.split(',')[1];
                    const binaryData = atob(base64Data);
                    const bytes = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) {
                        bytes[i] = binaryData.charCodeAt(i);
                    }
                    const blob = new Blob([bytes], { type: fileInfo.type });
                    const uploadFormData = new FormData();
                    Object.entries(presigned_post.fields).forEach(([key, value]) => {
                        uploadFormData.append(key, value);
                    });
                    uploadFormData.append('file', blob);
                    const s3Response = await fetch(presigned_post.url, {
                        method: 'POST',
                        body: uploadFormData
                    });

                    if (!s3Response.ok) {
                        throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`);
                    }
                    presigned_url = presigned_download_url;
                    s3_key = response_s3_key;
                }

                let simulationData = {
                    presigned_url: presigned_url,
                    s3_key: s3_key,
                    settings: formattedSettings
                };

                console.log("Starting background simulation...");
                const responseStartSimulation = await apiPost("UX_HTTP_API", `/esid-25-006/start-simulation`, simulationData);
                console.log("Simulation started successfully", responseStartSimulation);

            } catch (error) {
                console.error("Background simulation error:", error);
            }
        })();
    };


    const renderInputByType = (item, config, category) => {
        const { key, variableName, displayName, detailedName, defaultValue, type, additionalInfo, selections } = item;
        const currentValue = settingsValues[key];
        
        // Hide pv1_azimut_deg
        if (variableName === "pv1_azimut_deg") {
            return null;
        }
        
        // Hide non-radio button settings in PV configuration if "Based on input file" is selected
        const pvBasedOnInputFile = isPVBasedOnInputFile(category);
        if (pvBasedOnInputFile && type?.toLowerCase() !== 'radio button' && key !== 'pv1_radio_button') {
            return null;
        }

        // Hide "baseload" setting if "non-EV consumption" is not "Based on settings"
        if (key === 'consumption_other_power_mw_baseload' && category === 'electricity_consumption') {
            if (!isNonEVConsumptionBasedOnSettings()) {
                return null;
            }
        }

        if(category === 'electricity_market') {
            
        }
        // Hide fixed EV charging settings if "Fixed EV charging" is not "Based on settings"
        if (category === 'electricity_consumption' &&
            (key === 'consumption_ev_fixed_power_per_charger_mw' ||
                key === 'consumption_ev_fixed_number_of_chargers_unitless' ||
                key === 'consumption_ev_fixed_charging_hours_unitless')) {
            if (!isFixedEVChargingBasedOnSettings()) {
                return null;
            }
        }

        const componentColor = {
            color: config.color,
            border: config.borderColor,
            bg: `${config.color}25`
        };

        const getOptions = () => {
            if (selections && Array.isArray(selections)) {
                return selections;
            }
            if (additionalInfo && additionalInfo.includes(';')) {
                return additionalInfo.split(';').map(opt => opt.trim());
            }
            return [];
        };

        if (type?.toLowerCase() === 'radio button') {
            const options = getOptions();
            const needsUpload = needsFileUpload(key, currentValue);

            return (
                <Grid item xs={12} sm={4} md={3} key={key}>
                    <Box sx={{
                        border: `1.5px solid ${needsUpload ? '#ed6c02' : componentColor.border}`,
                        padding: '18px',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            borderColor: needsUpload ? '#ed6c02' : componentColor.color,
                        }
                    }}>
                        <FormControl fullWidth>
                            <FormLabel
                                id={`${key}-radio-label`}
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&.Mui-focused': {
                                        color: componentColor.color
                                    }
                                }}
                            >
                                {displayName}

                                {detailedName && detailedName !== '/' && (
                                    <Tooltip title={detailedName} arrow placement="top">
                                        <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby={`${key}-radio-label`}
                                name={`${key}-radio-group`}
                                value={currentValue !== undefined && currentValue !== null ? currentValue : (defaultValue || '')}
                                onChange={(e) => handleChange(key, e.target.value)}
                            >
                                {options.length > 0 ? (
                                    options.map((option, idx) => (
                                        <FormControlLabel
                                            key={idx}
                                            value={option}
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: componentColor.color,
                                                        '&.Mui-checked': {
                                                            color: componentColor.color,
                                                        }
                                                    }}
                                                />
                                            }
                                            label={option}
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.9rem'
                                                }
                                            }}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        No options available
                                    </Typography>
                                )}
                            </RadioGroup>
                            {needsUpload && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, ml: 0.5 }}>
                                    <WarningAmberIcon sx={{ fontSize: '16px', color: '#ed6c02' }} />
                                    <Typography variant="caption" sx={{ color: '#ed6c02', fontSize: '0.75rem', fontWeight: 500 }}>
                                        File upload required for this selection
                                    </Typography>
                                </Box>
                            )}
                        </FormControl>
                    </Box>
                </Grid>
            );
        }

        if (type?.toLowerCase() === 'select button' || type?.toLowerCase() === 'drop-down') {
            const options = getOptions();
            return (
                <Grid item xs={12} sm={4} md={3} key={key}>
                    <Box sx={{
                        backgroundColor: componentColor.bg,

                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            boxShadow: `0 8px 24px ${componentColor.bg}`,
                        }
                    }}>
                        <FormControl fullWidth>
                            <InputLabel
                                id={`${key}-select-label`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&.Mui-focused': {
                                        color: componentColor.color
                                    }
                                }}
                            >
                                {displayName}
                                {detailedName && detailedName !== '/' && (
                                    <Tooltip title={detailedName} arrow placement="top">
                                        <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                            </InputLabel>
                            <Select
                                labelId={`${key}-select-label`}
                                id={`${key}-select`}
                                value={currentValue !== undefined && currentValue !== null ? currentValue : (defaultValue || '')}
                                label={variableName}
                                onChange={(e) => handleChange(key, e.target.value)}
                                sx={{
                                    backgroundColor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: componentColor.border,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: componentColor.color,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: componentColor.color,
                                        borderWidth: '2px'
                                    }
                                }}
                            >
                                {options.length > 0 ? (
                                    options.map((option, idx) => (
                                        <MenuItem key={idx} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        No options available
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
            );
        }

        if (type.toLowerCase() === 'scheduler') {
            const isVisible = visibleSchedulers.has(key);

            return (
                <Grid item xs={12} key={key} >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: componentColor.color }}>
                            {displayName}
                        </Typography>
                        {detailedName && detailedName !== '/' && (
                            <Tooltip title={detailedName} arrow placement="top">
                                <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const newVisible = new Set(visibleSchedulers);
                                if (isVisible) {
                                    newVisible.delete(key);
                                } else {
                                    newVisible.add(key);
                                }
                                setVisibleSchedulers(newVisible);
                            }}
                            sx={{
                                ml: 1,
                                borderColor: componentColor.color,
                                color: componentColor.color,
                                '&:hover': {
                                    borderColor: componentColor.color,
                                    backgroundColor: componentColor.bg,
                                }
                            }}
                        >
                            {isVisible ? 'Hide Scheduler' : 'Show Scheduler'}
                        </Button>
                    </Box>
                    {isVisible && (
                        <SchedulerTable
                            label={displayName}
                            value={currentValue !== undefined ? currentValue : (defaultValue || '{}')}
                            onChange={(value) => handleChange(key, value)}
                        />
                    )}
                </Grid>
            );
        }


        if (type === 'number' || variableName?.includes('_mw') || variableName?.includes('_deg') ||
            variableName?.includes('_mva') || variableName?.includes('_percentage') ||
            variableName?.includes('_eur') || variableName?.includes('_unitless')) {
            return (
                <Grid item xs={12} sm={4} md={3} key={key}>
                    <Box sx={{
                        borderRadius: '14px',
                        //border: `1.5px solid ${componentColor.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            boxShadow: `0 8px 24px ${componentColor.bg}`,
                        }
                    }}>
                        <TextField
                            label={displayName}
                            value={currentValue !== undefined ? currentValue : (defaultValue || '')}
                            onChange={(e) => handleChange(key, parseFloat(e.target.value) || 0)}
                            fullWidth
                            disabled={variableName === "pv1_azimut_deg"}
                            type="number"
                            InputProps={{
                                step: 'any',
                                endAdornment: detailedName && detailedName !== '/' ? (
                                    <Tooltip title={detailedName} arrow placement="top">
                                        <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help', ml: 1 }} />
                                    </Tooltip>
                                ) : null, sx: {
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                }
                            }}
                            FormHelperTextProps={{
                                sx: { fontSize: '0.75rem', ml: 0.5 }
                            }}

                        />
                    </Box>
                </Grid>
            );
        }

        if (variableName?.includes('_date') || variableName?.includes('_iso')) {
            const isRequired = isFieldRequired(key, currentValue);

            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            // Set minDate to the first day of 36 months before current month
            const minDate = dayjs(new Date(currentYear, currentMonth - 36, 1));

            // Set maxDate to the last day of current month
            const maxDate = dayjs(new Date(currentYear, currentMonth + 1, 0));

            // Determine if this is a "from" or "to" date field
            const isFromDate = key.includes('from');
            const isToDate = key.includes('to');

            // Handle date change with proper start/end of month logic
            const handleDateChange = (date) => {
                if (!date) {
                    handleChange(key, null);
                    return;
                }

                let adjustedDate = dayjs(date);

                if (isFromDate) {
                    // Set to first day of month at 00:00:00
                    adjustedDate = adjustedDate.startOf('month').startOf('day');
                } else if (isToDate) {
                    // Set to last day of month at 23:59:59
                    adjustedDate = adjustedDate.endOf('month');
                }

                // Format as ISO 8601 string without timezone offset (backend expects tz-naive)
                const formattedDate = adjustedDate.format('YYYY-MM-DDTHH:mm:ss');
                
                console.log('Date formatted (tz-naive):', formattedDate);
                handleChange(key, formattedDate);
            };

            // Format display text
            const getDisplayText = () => {
                if (!currentValue) return null;
                const date = dayjs(currentValue);
                if (isFromDate) {
                    return `${date.format('DD-MM-YYYY')}`;
                } else if (isToDate) {
                    return `${date.format('DD-MM-YYYY')}`;
                }
                return null;
            };

            return (
                <Grid item xs={12} sm={4} md={3} key={key} justifyContent="center" alignItems="center">
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '80px',
                        gap: 1
                    }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker', 'DatePicker', 'DatePicker']}>
                                <DatePicker
                                    label={displayName + (isRequired ? ' *' : '')}
                                    views={['month', 'year']}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    value={currentValue !== undefined ? dayjs(currentValue) : (defaultValue ? dayjs(defaultValue) : null)}
                                    onChange={handleDateChange}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                        {currentValue && (
                            <Tooltip
                                title={
                                    <Box sx={{ p: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                            📅 {getDisplayText()}
                                        </Typography>

                                    </Box>
                                }
                                arrow
                                placement="right"
                                sx={{
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: componentColor.color,
                                        fontSize: '0.9rem',
                                        padding: '12px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                    },
                                    '& .MuiTooltip-arrow': {
                                        color: componentColor.color
                                    }
                                }}
                            >
                                <InfoIcon
                                    sx={{
                                        fontSize: '20px',
                                        color: componentColor.color,
                                        cursor: 'help',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            color: '#483F5E',
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Grid>
            );
        }
        return (
            <Grid item xs={12} sm={4} md={3} key={key}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        boxShadow: `0 4px 8px ${componentColor.bg}`,
                    },
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: variableName === 'simulation_unique_id' ? '85px' : '60px',
                }}>
                    <TextField
                        label={displayName}
                        value={currentValue !== undefined ? currentValue : (defaultValue || '')}
                        onChange={(e) => handleChange(key, e.target.value)}
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            endAdornment: detailedName && detailedName !== '/' ? (
                                <Tooltip title={detailedName} arrow placement="top">
                                    <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary', cursor: 'help', ml: 1 }} />
                                </Tooltip>
                            ) : null
                        }}
                        FormHelperTextProps={{
                            sx: { fontSize: '0.75rem', ml: 0.5 }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',

                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: componentColor.color,
                            },
                        }}
                    />
                </Box>
            </Grid>
        );
    };

    const isFieldRequired = (key, value) => {
        if (key.includes('simulation_unique_id') || key.includes('simulation_from_date_iso') || key.includes('simulation_to_date_iso')) {
            return !value || value === '';
        }
        return false;
    };

    const needsFileUpload = (key, value) => {
        if (value === "Based on input file") {
            if (key.toLowerCase().includes('pv') || key.toLowerCase().includes('generation')) {
                return !uploadedFiles.pvGeneration;
            } else if (key.toLowerCase().includes('consumption')) {
                return !uploadedFiles.consumption;
            } else if (key.toLowerCase().includes('price') || key.toLowerCase().includes('market')) {
                return !uploadedFiles.prices;
            } else {
                return !uploadedFiles.data;
            }
        }
        return false;
    };

    // Check if "non-EV consumption" radio button has "Based on settings" selected
    const isNonEVConsumptionBasedOnSettings = () => {
        const consumptionValue = settingsValues['consumption_other_radio_button'];
        return consumptionValue === "Based on settings";
    };

    // Check if "Fixed EV charging" radio button has "Based on settings" selected
    const isFixedEVChargingBasedOnSettings = () => {
        const fixedEVValue = settingsValues['consumption_ev_fixed_radio_button'];
        return fixedEVValue === "Based on settings";
    };

    // Check if PV configuration has "Based on input file" selected
    const isPVBasedOnInputFile = (category) => {
        const pvCategories = ['Photovoltaïc installation', 'pv_configuration'];
        if (!pvCategories.includes(category)) {
            return false;
        }

        // Check if PV radio button has "Based on input file" selected
        const pvValue = settingsValues['pv1_radio_button'];
        return pvValue === "Based on input file";
    };

    // Render default category layout
    // If category has a radio button: radio button on left, other inputs on right in 2 rows x 3 columns
    // If no radio button: standard grid layout
    const renderDefaultCategoryLayout = (items, config, category) => {
        const radioButtonItem = items.find(item => item.type?.toLowerCase() === 'radio button');
        const otherItems = items.filter(item => item.type?.toLowerCase() !== 'radio button');

        // Check if this is the simulation config category and get date values
        const isSimulationConfig = category === 'Start simulation ' || category === 'simulation_config';
        const fromDateItem = items.find(item => item.key.includes('from_date'));
        const toDateItem = items.find(item => item.key.includes('to_date'));
        const fromDateValue = fromDateItem ? settingsValues[fromDateItem.key] : null;
        const toDateValue = toDateItem ? settingsValues[toDateItem.key] : null;

        if (category === 'electricity_market') {

            //reoprder the items based on the offtake and injection parameters
            let offtakeItems= items.filter(item => item.key.includes('offtake'));
            let injectionItems= items.filter(item => item.key.includes('injection'));
            let reorderOtherItems = items.filter(item => !item.key.includes('offtake') && !item.key.includes('injection'));
            items = [...offtakeItems, ...injectionItems, ...reorderOtherItems];


            if (radioButtonItem) {
                otherItems.length = 0;
                const reorderedOtherItems = items.filter(item => item.type?.toLowerCase() !== 'radio button');
                otherItems.push(...reorderedOtherItems);
            }

        }
        // If no radio button, use normal grid layout
        if (!radioButtonItem) {
            return (
                <>
                    <Grid container spacing={3}>
                        {items.map((item) => renderInputByType(item, config, category)).filter(Boolean)}
                    </Grid>
                </>
            );
        }

        // If has radio button: radio button on left (3 columns), other inputs on right (9 columns) in 2x3 grid
        return (
            <>
                <Grid container spacing={3} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {/* Left side: Radio button */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{ height: '100%', '& .MuiGrid-item': { maxWidth: '100% !important', width: '100% !important', flexBasis: '100% !important' } }}>
                            {renderInputByType(radioButtonItem, config, category)}
                        </Box>
                    </Grid>



                    {/* Right side: Other inputs in 2 rows x 3 columns grid */}
                    <Grid item xs={12} md={9}>
                        <Grid container spacing={2}>
                            {otherItems.map((item) => {
                                const rendered = renderInputByType(item, config, category);
                                if (!rendered) return null;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={item.key}>
                                        <Box sx={{ '& .MuiGrid-item': { maxWidth: '100% !important', width: '100% !important', flexBasis: '100% !important' } }}>
                                            {rendered}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    };

    // Render Electricity Consumption with special column-based layout
    const renderElectricityConsumptionLayout = (items, config, category) => {
        // Separate items into groups
        const blueGroup = []; // Column 1: non-EV consumption + baseload
        const greenGroup = []; // Column 2: Fixed EV charging + dependents
        const otherItems = []; // Column 3: Other items (non-scheduler)
        const schedulers = []; // Bottom: All schedulers

        items.forEach(item => {
            const key = item.key;
            const type = item.type?.toLowerCase();

            // Schedulers go to bottom
            if (type === 'scheduler') {
                schedulers.push(item);
            }
            // Blue group (Row 1): non-EV consumption
            else if (key === 'consumption_other_radio_button' || key === 'consumption_other_power_mw_baseload') {
                blueGroup.push(item);
            }
            // Green group (Row 2): Fixed EV charging
            else if (key === 'consumption_ev_fixed_radio_button' ||
                key === 'consumption_ev_fixed_power_per_charger_mw' ||
                key === 'consumption_ev_fixed_number_of_chargers_unitless' ||
                key === 'consumption_ev_fixed_charging_hours_unitless') {
                greenGroup.push(item);
            }
            // Others (Row 3)
            else {
                otherItems.push(item);
            }
        });

        return (
            <>
                {/* Sort groups: radio button first */}
                {(() => {
                    const sortByRadioButton = (a, b) => {
                        const aIsRadio = a.type?.toLowerCase() === 'radio button';
                        const bIsRadio = b.type?.toLowerCase() === 'radio button';
                        if (aIsRadio && !bIsRadio) return -1;
                        if (!aIsRadio && bIsRadio) return 1;
                        return 0;
                    };

                    return (
                        <>
                            <Grid container spacing={3}>
                                {/* Column 1: Blue group (non-EV consumption + baseload) */}
                                <Grid item xs={12} md={3.8}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {blueGroup.sort(sortByRadioButton).map((item) => {
                                            const rendered = renderInputByType(item, config, category);
                                            if (!rendered) return null;
                                            return (
                                                <Box key={item.key} sx={{ width: '100%', '& .MuiGrid-item': { maxWidth: '100% !important', width: '100% !important', flexBasis: '100% !important' } }}>
                                                    {rendered}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Grid>

                                {/* Divider between column 1 and 2 */}
                                <Grid item xs={0} md="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{
                                            height: '100%',
                                            alignSelf: 'center',
                                            borderColor: 'divider',
                                            borderWidth: 1
                                        }}
                                    />
                                </Grid>

                                {/* Column 2: Green group (Fixed EV charging + dependents) */}
                                <Grid item xs={12} md={3.8}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {greenGroup.sort(sortByRadioButton).map((item) => {
                                            const rendered = renderInputByType(item, config, category);
                                            if (!rendered) return null;
                                            return (
                                                <Box key={item.key} sx={{ width: '100%', '& .MuiGrid-item': { maxWidth: '100% !important', width: '100% !important', flexBasis: '100% !important' } }}>
                                                    {rendered}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Grid>

                                {/* Divider between column 2 and 3 */}
                                <Grid item xs={0} md="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{
                                            height: '100%',
                                            alignSelf: 'center',
                                            borderColor: 'divider',
                                            borderWidth: 1
                                        }}
                                    />
                                </Grid>

                                {/* Column 3: Other items */}
                                <Grid item xs={12} md={3.8}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {otherItems.map((item) => {
                                            const rendered = renderInputByType(item, config, category);
                                            if (!rendered) return null;
                                            return (
                                                <Box key={item.key} sx={{ width: '100%', '& .MuiGrid-item': { maxWidth: '100% !important', width: '100% !important', flexBasis: '100% !important' } }}>
                                                    {rendered}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Bottom: All schedulers */}
                            {schedulers.length > 0 && (
                                <Grid container spacing={3} sx={{ mt: 2 }}>
                                    {schedulers.map((item) => renderInputByType(item, config, category))}
                                </Grid>
                            )}
                        </>
                    );
                })()}
            </>
        );
    };

    return (
        <>
            <Modal open={open} onClose={closeModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    zIndex: 998,
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <ModalHeader>
                        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            Start Simulation
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                            Configure your simulation parameters and upload data files
                        </Typography>
                        <CloseButton onClick={() => onClose()}>
                            <CloseIcon />
                        </CloseButton>
                    </ModalHeader>

                    <Box sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 4,
                        '&::-webkit-scrollbar': {
                            width: '10px',
                            backgroundColor: '#f5f5f5',
                        },
                        '&::-webkit-scrollbar-track': {
                            borderRadius: '8px',
                            backgroundColor: '#f5f5f5',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '8px',
                            backgroundColor: 'rgba(72,63,94,0.2)',
                            '&:hover': {
                                backgroundColor: 'rgba(72,63,94,0.4)',
                            },
                        },
                    }}>


                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                mb: 2
                            }}
                        >
                            {roles.map((role, index) => (
                                <Tab
                                    key={role}
                                    label={role.toUpperCase()}
                                    sx={{
                                        textTransform: 'capitalize',
                                        fontWeight: 'medium'
                                    }}
                                />
                            ))}
                        </Tabs>
                        {roles.map((role, index) => (
                            <Box
                                key={role}
                                hidden={currentTab !== index}
                                sx={{ mt: 2 }}
                            >
                                {Object.entries(roleBasedSettings[role]).map(([category, items]) => {
                                    const config = categoryConfig[category] || {
                                        icon: SettingsIcon,
                                        color: '#757575',
                                        borderColor: '#E0E0E0',
                                        backgroundColor: 'rgba(117, 117, 117, 0.06)'
                                    };
                                    const IconComponent = config.icon;

                                    return (
                                        <CategoryContainer
                                            key={category}
                                            borderColor={config.borderColor}
                                            //bgcolor={config.backgroundColor}
                                            elevation={1}
                                        >
                                            <CategoryHeader color={config.color}>
                                                <Box sx={{
                                                    background: config.color,
                                                    borderRadius: '14px',
                                                    width: 48,
                                                    height: 48,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    boxShadow: `0 4px 16px ${config.color}35`,
                                                    '&:hover': {
                                                        boxShadow: `0 6px 20px ${config.color}50`,
                                                    }
                                                }}>
                                                    <IconComponent sx={{ fontSize: 24 }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: config.color,
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {formatCategoryName(category)}
                                                    </Typography>

                                                </Box>
                                            </CategoryHeader>
                                            {category === 'Local electricity consumption' || category === 'electricity_consumption' ? (
                                                // Special 3-column layout for Electricity Consumption
                                                renderElectricityConsumptionLayout(items, config, category)
                                            ) : (
                                                // Default layout for all other categories
                                                renderDefaultCategoryLayout(items, config, category)
                                            )}

                                        </CategoryContainer>
                                    );
                                })}
                            </Box>
                        ))}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <QuickSimulationSetup
                                        settingsValues={settingsValues}
                                        handleChange={handleChange}
                                        generateSimulationId={generateSimulationId}
                                        uploadedFiles={uploadedFiles}
                                        handleFileUpload={handleFileUpload}
                                        fileInputRefs={fileInputRefs}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box sx={{
                        p: 3,
                        borderTop: '2px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 3,
                        bgcolor: 'background.paper',
                        background: 'linear-gradient(to right, rgba(99, 87, 129, 0.02) 0%, rgba(99, 87, 129, 0.05) 100%)'
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', flex: 1 }}>
                            Review your settings before starting the simulation
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={closeModal}
                                startIcon={<CancelIcon />}
                                sx={{
                                    width: '180px',
                                    height: '48px',
                                    borderWidth: '2px',
                                    borderColor: '#9e9e9e',
                                    color: '#616161',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderWidth: '2px',
                                        borderColor: '#757575',
                                        backgroundColor: 'rgba(158, 158, 158, 0.08)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSimulate}
                                disabled={isLoading || loadingApi}
                                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                sx={{
                                    width: '220px',
                                    height: '52px',
                                    bgcolor: '#635781',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    boxShadow: '0 6px 20px rgba(99, 87, 129, 0.35)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: '#483F5E',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 28px rgba(99, 87, 129, 0.5)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#b0b0b0',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {isLoading ? 'Starting Simulation...' : 'Start Simulation'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

        </>
    );
};

export default SimulationSettingsModal;